import { IFluidObject } from '@fluidframework/core-interfaces';
import { IResolvedUrl } from '@fluidframework/driver-definitions';
import { ShareService, Persona } from '@fluidx/office-fluid-types';
import { PostMessageSender, PostMessageHandlerFactory } from '../Shared/PostMessage';
import {
  CheckUserAccessMessage,
  CheckUserAccessReturnedMessage,
  GrantUserAccessMessage,
  GrantUserAccessReturnedMessage,
  ShareLinkMessage,
  ShareLinkReturnedMessage,
  PairingValue,
  GetPermissionsMessage,
  GetPermissionsReturnedMessage
} from '../MessageTypes';
import { serializeError, deserializeError } from '@fluidx/utilities';

export function getHostSideShareService(
  scope: IFluidObject
): {
  scopePairingValue?: PairingValue<Promise<ShareService>>;
  scopeHandler?: PostMessageHandlerFactory;
} {
  const shareServicePromise = scope.ShareServicePromise;
  if (!shareServicePromise) {
    return {};
  }

  return {
    scopePairingValue: { found: true },
    scopeHandler: (sender: PostMessageSender) => {
      return {
        ['CheckUserAccess']: async (message) => {
          const payload = (message as CheckUserAccessMessage).payload;
          let reply: CheckUserAccessReturnedMessage;

          try {
            reply = {
              payload: { result: await (await shareServicePromise).checkUserAccess(payload) }
            };
          } catch (e) {
            reply = {
              error: true,
              payload: serializeError(e)
            };
          }

          sender.sendReply(message, reply);
        },
        ['GrantUserAccess']: async (message) => {
          const payload = (message as GrantUserAccessMessage).payload;
          let reply: GrantUserAccessReturnedMessage;

          try {
            reply = {
              payload: { result: await (await shareServicePromise).grantPermissions(payload) }
            };
          } catch (e) {
            reply = {
              error: true,
              payload: serializeError(e)
            };
          }

          sender.sendReply(message, reply);
        },
        ['ShareLink']: async (message) => {
          const payload = (message as ShareLinkMessage).payload;
          let reply: ShareLinkReturnedMessage;

          try {
            reply = {
              payload: { result: await (await shareServicePromise).shareLink(payload) }
            };
          } catch (e) {
            reply = {
              error: true,
              payload: serializeError(e)
            };
          }

          sender.sendReply(message, reply);
        },
        ['GetPermissions']: async (message) => {
          const payload = (message as GetPermissionsMessage).payload;
          let reply: GetPermissionsReturnedMessage;

          try {
            reply = {
              payload: { result: await (await shareServicePromise).getPermissions(payload) }
            };
          } catch (e) {
            reply = {
              error: true,
              payload: serializeError(e)
            };
          }

          sender.sendReply(message, reply);
        }
      };
    }
  };
}

export function getGuestSideShareService(sender: PostMessageSender): Promise<ShareService> {
  return Promise.resolve({
    get ShareServicePromise(): Promise<ShareService> {
      return Promise.resolve(this);
    },
    checkUserAccess({
      resolvedUrl,
      userPrincipalName
    }: {
      resolvedUrl: IResolvedUrl;
      userPrincipalName: string;
    }): Promise<boolean> {
      const message: CheckUserAccessMessage = {
        type: 'CheckUserAccess',
        payload: { resolvedUrl, userPrincipalName }
      };
      return sender.sendWithReturn(message).then((reply) => {
        return resolveOrRejectReply(reply as CheckUserAccessReturnedMessage);
      });
    },
    grantPermissions: ({
      resolvedUrl,
      userPrincipalNames
    }: {
      resolvedUrl?: IResolvedUrl;
      userPrincipalNames: string[];
    }): Promise<boolean> => {
      const message: GrantUserAccessMessage = {
        type: 'GrantUserAccess',
        payload: { resolvedUrl, userPrincipalNames }
      };
      return sender.sendWithReturn(message).then((reply) => {
        return resolveOrRejectReply(reply as GrantUserAccessReturnedMessage);
      });
    },
    shareLink: ({
      resolvedUrl,
      componentPath
    }: {
      resolvedUrl?: IResolvedUrl;
      componentPath?: string;
    }): Promise<boolean> => {
      const message: ShareLinkMessage = {
        type: 'ShareLink',
        payload: { resolvedUrl, componentPath }
      };
      return sender.sendWithReturn(message).then((reply) => {
        return resolveOrRejectReply(reply as ShareLinkReturnedMessage);
      });
    },
    getPermissions: ({ resolvedUrl }: { resolvedUrl?: IResolvedUrl }): Promise<any> => {
      const message: GetPermissionsMessage = {
        type: 'GetPermissions',
        payload: { resolvedUrl }
      };
      return sender.sendWithReturn(message).then((reply) => {
        return resolveOrRejectReply(reply as GetPermissionsReturnedMessage);
      });
    }
  });
}

export function resolveOrRejectReply<T extends boolean | Persona[]>(
  reply:
    | CheckUserAccessReturnedMessage
    | GrantUserAccessReturnedMessage
    | ShareLinkReturnedMessage
    | GetPermissionsReturnedMessage
): Promise<T> {
  const payload = reply.payload;

  if (typeof payload !== 'string') {
    return Promise.resolve(payload.result as T);
  }

  if (reply.error) {
    return Promise.reject(deserializeError(payload));
  }

  return Promise.resolve(false as T);
}
// cspell:ignore UPNs
